import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "./ui/button";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: "Services", path: "/services" },
    { name: "Blog", path: "/blog" },
    { name: "About Us", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="top-bar text-white py-2 hidden lg:block" data-testid="top-bar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <a href="tel:+918770828302" className="flex items-center gap-2 hover:text-cyan-300 transition-colors">
                <Phone className="w-4 h-4" />
                <span>+91-8770828302</span>
              </a>
              <span className="text-cyan-300">|</span>
              <a href="tel:+919303772139" className="flex items-center gap-2 hover:text-cyan-300 transition-colors">
                <Phone className="w-4 h-4" />
                <span>+91-9303772139</span>
              </a>
              <a href="mailto:vividh2osolutions@gmail.com" className="flex items-center gap-2 hover:text-cyan-300 transition-colors">
                <Mail className="w-4 h-4" />
                <span>vividh2osolutions@gmail.com</span>
              </a>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              <span>C-125, Vidhya Nagar, Hoshangabad Road, Bhopal</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <header 
        className={`sticky top-0 z-50 bg-white transition-all duration-300 ${
          isScrolled ? "shadow-lg" : "shadow-sm"
        }`} 
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo">
              <img 
                src="/logo-hd.png" 
                alt="VIVID - We restore purity" 
                className="h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 text-sm font-medium transition-all rounded-md ${
                    isActive(link.path)
                      ? "text-[#0d47a1] bg-blue-50"
                      : "text-gray-700 hover:text-[#0d47a1] hover:bg-blue-50"
                  }`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-3">
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-[#0d47a1] to-[#1976d2] hover:from-[#0a3d8f] hover:to-[#1565c0] text-white shadow-lg" data-testid="get-quote-btn">
                  Get Free Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 text-gray-700"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-btn"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isOpen && (
            <div className="lg:hidden py-4 border-t border-gray-100" data-testid="mobile-nav">
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? "bg-blue-50 text-[#0d47a1]"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="px-4 py-3 space-y-2 border-t border-gray-100 mt-2">
                  <a href="tel:+918770828302" className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4 text-[#0d47a1]" />
                    +91-8770828302
                  </a>
                  <a href="mailto:vividh2osolutions@gmail.com" className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail className="w-4 h-4 text-[#0d47a1]" />
                    vividh2osolutions@gmail.com
                  </a>
                </div>
                <Link to="/contact" onClick={() => setIsOpen(false)} className="mx-4 mt-2">
                  <Button className="w-full bg-gradient-to-r from-[#0d47a1] to-[#1976d2] text-white">
                    Get Free Quote
                  </Button>
                </Link>
              </nav>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Navbar;
